import b from 'b_';
import cx from 'classnames';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';

import { hasParent } from '../../utils/dom';
import { getViewportSize } from '../../utils/viewport';

import Calendar from './__calendar/datetimepicker__calendar';
import Input from './__input/datetimepicker__input';
import Popup from './__popup/datetimepicker__popup';
import { MODES, VIEWS } from './constants';
import Timer from './timer';
import { getTodayMonth, getTodayYear } from './utils';

import './index.scss';

const DATE_TIME_FOCUS_SEQUENCE = ['day', 'month', 'year', 'hour', 'minute'];
const DATE_FOCUS_SEQUENCE = ['day', 'month', 'year'];
const THROTTLE_WAIT = 300;

class Datetimepicker extends PureComponent {
    static propTypes = {
        preventCrossing: PropTypes.bool,
        centered: PropTypes.bool,
        date: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        disabled: PropTypes.bool,
        enabledDates: PropTypes.arrayOf(PropTypes.number),
        getNow: PropTypes.func,
        mix: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
        mode: PropTypes.oneOf(Object.values(MODES)),
        view: PropTypes.oneOf(Object.values(VIEWS)),
        onChange: PropTypes.func,
        withNow: PropTypes.bool,
    };

    static defaultProps = {
        getNow: () => Promise.resolve(new Date()),
        mode: MODES.DATETIME,
        withNow: false,
        preventCrossing: false,
        view: VIEWS.WITH_POPUP,
    };

    static MODES = MODES;
    static VIEWS = VIEWS;

    constructor(...args) {
        super(...args);

        const { date, withNow } = this.props;

        this.isClickEventTriggered = false;
        this.popup = React.createRef();

        this.state = {
            today: new Date(),
            isTimeFocused: false,
            isDateFocused: false,
            isNowSelected: false,
            day: '',
            month: '',
            year: '',
            hour: '',
            minute: '',
            monthPopup: `${getTodayMonth()}`,
            yearPopup: `${getTodayYear()}`,
        };

        if (withNow && date === 'now') {
            this.state = {
                ...this.state,
                isNowSelected: true,
            };
        }

        if (date instanceof Date) {
            this.state = {
                ...this.state,
                ...this.extractDatePartsForState(date),
            };
        }
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.onMouseDown);
        this.props.getNow().then((today) => this.setState({ today }));

        this.calendarLayer = document.createElement('div');
        this.calendarLayer.className = 'calendar-layer';
        document.body.appendChild(this.calendarLayer);

        if (this.props.preventCrossing) {
            window.addEventListener('scroll', this.handleResize, true);
        }

        window.addEventListener('resize', this.handleResize);
        this.handleResize();
    }

    UNSAFE_componentWillReceiveProps({ date: newDate }) {
        const { date } = this.props;

        if (`${newDate}` === `${date}`) {
            return;
        }

        if (newDate === 'now') {
            this.setNow();

            return;
        }

        if (newDate instanceof Date) {
            this.setState({ ...this.extractDatePartsForState(newDate) });
        }
    }

    componentDidUpdate() {
        this.handleResize();
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.onMouseDown);
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('scroll', this.handleResize);
        document.body.removeChild(this.calendarLayer);
    }

    controls = {};

    handleResize = throttle(() => {
        const { centered } = this.props;
        const rootNode = ReactDOM.findDOMNode(this.root);

        if (!rootNode) {
            return;
        }

        const { height, left, top, width } = rootNode.getBoundingClientRect();
        let topCoord = top + window.scrollY + height;
        let leftCoord = centered ? left - (320 - width) / 2 : left;

        if (this.props.preventCrossing) {
            const { width: windowWidth, height: windowHeight } = getViewportSize();
            const popupHeight = this.popup.clientHeight;
            const popupWidth = this.popup.clientWidth;

            if (topCoord + popupHeight > windowHeight + window.scrollY) {
                topCoord = windowHeight + window.scrollY - popupHeight;
            } else if (topCoord < 0) {
                topCoord = 0;
            }

            if (leftCoord + popupWidth > windowWidth) {
                leftCoord = windowWidth - popupWidth;
            } else if (leftCoord < 0) {
                leftCoord = 0;
            }
        }

        if (topCoord !== this.state.top || left !== this.state.left) {
            this.setState({ left: leftCoord, top: topCoord });
        }
    }, THROTTLE_WAIT);

    extractDatePartsForState = (date) => ({
        day: `0${date.getDate()}`.slice(-2),
        month: `0${date.getMonth() + 1}`.slice(-2),
        year: `${date.getFullYear()}`,
        hour: `0${date.getHours()}`.slice(-2),
        minute: `0${date.getMinutes()}`.slice(-2),
        monthPopup: `${date.getMonth()}`,
        yearPopup: `${date.getFullYear()}`,
    });

    onMouseDown = (e) => {
        this.isClickEventTriggered = true;

        this.blurAll(e);
    };

    getSelectedDate() {
        const { day, month, year, hour, minute } = this.state;

        if (!day && !month && !year && !hour && !minute) {
            return;
        }

        return new Date(year, month - 1, day, hour, minute); // eslint-disable-line consistent-return
    }

    // eslint-disable-next-line max-statements
    blurAll = (event, force) => {
        const { withNow, view } = this.props;
        const { isDateFocused, isTimeFocused } = this.state;
        const isCalendarClicked = Boolean(event && hasParent(event.target, this.calendarLayer));

        const shouldHandleUnfocus = view !== VIEWS.WITHOUT_POPUP && !isDateFocused && !isTimeFocused;

        if (shouldHandleUnfocus || (!force && hasParent(event.target, this.root)) || isCalendarClicked) {
            return;
        }

        const selectedDate = this.getSelectedDate();
        const newState = {
            isDateFocused: false,
            isTimeFocused: false,
        };

        if (!selectedDate) {
            this.setState(newState);
            return;
        }

        if (!withNow) {
            this.setState(newState);

            this.onChange(selectedDate);
            return;
        }

        this.isSelectedDateInPast().then((isInPast) => {
            this.setState({ ...newState, isNowSelected: isInPast });

            if (isInPast) {
                this.onChange('now');
                return;
            }

            if (this.timer) {
                this.timer.destroy();
            }

            this.timer = new Timer({
                eventDate: selectedDate,
                onEvent: this.setNow,
                sync: this.syncTimer,
            });

            this.onChange(selectedDate);
        });
    };

    isSelectedDateInPast = () => this.props.getNow().then((now) => this.getSelectedDate() < now);

    syncTimer = (date) => this.props.getNow().then((now) => date.getTime() - now);

    changeValue =
        (type, config) =>
        ({ target: { value } }) => {
            const {
                maxValue,
                minValue = 0,
                shortcutInterval,
                beforeSave: { field: beforeFieldSave = (v) => v, fieldPopup: beforeFieldPopupSave = (v) => v } = {},
            } = config;

            if (!value && this.state[type]) {
                this.setState({
                    [type]: '',
                    [`${type}Popup`]: '',
                });
            }

            if (!this.isValueValid(value, { minValue, maxValue })) {
                return;
            }

            const newValue = `${beforeFieldSave(value)}`;
            const newValuePopup = `${beforeFieldPopupSave(value)}`;

            if (shortcutInterval && value >= shortcutInterval[0] && value <= shortcutInterval[1]) {
                this.setState(
                    {
                        [type]: `0${newValue}`.slice(-2),
                        [`${type}Popup`]: newValuePopup,
                    },
                    () => this.focusNext(type)
                );
            }

            this.setState(
                {
                    [type]: newValue,
                    [`${type}Popup`]: newValuePopup,
                },
                () => {
                    if (newValue.length === `${maxValue}`.length) {
                        this.focusNext(type);
                    }
                }
            );
        };

    isValueValid = (value, { minValue, maxValue }) =>
        /^\d+$/.test(value) && value >= minValue && value <= maxValue && value.length <= `${maxValue}`.length;

    focusNext = (currentType) => {
        const { mode } = this.props;

        const focusSequence = mode === MODES.DATE ? DATE_FOCUS_SEQUENCE : DATE_TIME_FOCUS_SEQUENCE;
        const controlIndex = focusSequence.indexOf(currentType);
        const nextControl = focusSequence[controlIndex + 1];

        if (nextControl) {
            this.controls[nextControl].focus();
            this.controls[nextControl].select();

            return;
        }

        this.controls[currentType].blur();
    };

    refControl = (control) => (element) => {
        this.controls[control] = element;
    };

    refPopup = (element) => {
        this.popup = element;
    };

    focusDateInput = () => {
        this.controls.day.focus();
    };

    focusControl = (group, type) => () => {
        const groupToBlur = group === 'Time' ? 'Date' : 'Time';

        const { [`is${groupToBlur}Focused`]: hasAnotherGroupFocused } = this.state;

        this.setState({
            [`is${group}Focused`]: true,
            [`is${groupToBlur}Focused`]: false,
        });

        this.controls[type].select();

        if (hasAnotherGroupFocused === true) {
            this.onChange(this.getSelectedDate());
        }
    };

    blurControl = (type, length) => (event) => {
        const value = this.state[type];
        const { relatedTarget } = event;

        this.setState(
            {
                [type]: value ? value.padStart(length, '0') : '',
            },
            () => {
                if (this.isClickEventTriggered) {
                    this.isClickEventTriggered = false;

                    return;
                }

                // Проп relatedTarget содержит следующий фокусируемый элемент или null.
                // Для IE и onBlur всегда null ¯\_(ツ)_/¯ https://nda.ya.ru/3UFpVC
                if (
                    (relatedTarget === null || !hasParent(relatedTarget, this.root)) &&
                    !hasParent(document.activeElement, this.root)
                ) {
                    this.blurAll(null, true);
                }
            }
        );
    };

    changeCallbacks = {
        day: this.changeValue('day', { maxValue: 31, shortcutInterval: [4, 9] }),
        month: this.changeValue('month', {
            maxValue: 12,
            shortcutInterval: [2, 9],
            beforeSave: { fieldPopup: (v) => v - 1 },
        }),
        year: this.changeValue('year', { maxValue: 9999, minValue: 1 }),
        hour: this.changeValue('hour', { maxValue: 23, shortcutInterval: [3, 9] }),
        minute: this.changeValue('minute', { maxValue: 59, shortcutInterval: [6, 9] }),
    };

    focusCallbacks = {
        day: this.focusControl('Date', 'day'),
        month: this.focusControl('Date', 'month'),
        year: this.focusControl('Date', 'year'),
        hour: this.focusControl('Time', 'hour'),
        minute: this.focusControl('Time', 'minute'),
    };

    blurCallbacks = {
        day: this.blurControl('day', 2),
        month: this.blurControl('month', 2),
        year: this.blurControl('year', 4),
        hour: this.blurControl('hour', 2),
        minute: this.blurControl('minute', 2),
    };

    refCallbacks = {
        day: this.refControl('day'),
        month: this.refControl('month'),
        year: this.refControl('year'),
        hour: this.refControl('hour'),
        minute: this.refControl('minute'),
    };

    selectDay = (day) => () => {
        const { mode } = this.props;

        this.setState(
            {
                day: `0${day.date}`.slice(-2),
                month: `0${Number(day.month) + 1}`.slice(-2),
                year: `${day.year}`,
                monthPopup: `${day.month}`,
                yearPopup: `${day.year}`,
            },
            () => {
                if (mode === MODES.DATE) {
                    this.blurAll(null, true);
                } else {
                    this.focusNext('year');
                }
            }
        );
    };

    rootRefCallback = (element) => (this.root = element);

    revealNow = () =>
        this.props.getNow().then((now) => {
            this.setState({
                isNowSelected: false,
                isDateFocused: true,
                ...this.extractDatePartsForState(now),
            });

            this.controls.day.focus();
        });

    setNow = () => {
        this.setState({
            day: '',
            month: '',
            year: '',
            monthPopup: '',
            yearPopup: '',
            isNowSelected: true,
            isDateFocused: false,
            isTimeFocused: false,
        });

        this.onChange('now');
    };

    showPrevMonth = () => {
        const currentMonth = this.state.monthPopup;
        const currentYear = this.state.yearPopup;

        const monthPopup = Number(currentMonth) ? `${currentMonth - 1}` : '11';
        const yearPopup = monthPopup === '11' ? `${currentYear - 1}` : `${currentYear}`;

        this.setState({ monthPopup, yearPopup });
    };

    showNextMonth = () => {
        const currentMonth = this.state.monthPopup;
        const currentYear = this.state.yearPopup;

        const monthPopup = `${(Number(currentMonth) + 1) % 12}`;
        const yearPopup = Number(monthPopup) ? `${currentYear}` : `${Number(currentYear) + 1}`;

        this.setState({ monthPopup, yearPopup });
    };

    onChange(value) {
        const { onChange } = this.props;

        if (!value || !onChange) {
            return;
        }

        onChange(value);
    }

    renderCalendar = () => {
        const { yearPopup, monthPopup, today } = this.state;
        const { enabledDates } = this.props;

        return (
            <Calendar
                enabledDates={enabledDates}
                month={Number(monthPopup)}
                now={today}
                selectDay={this.selectDay}
                selectedDate={this.getSelectedDate()}
                showNextMonth={this.showNextMonth}
                showPrevMonth={this.showPrevMonth}
                year={Number(yearPopup)}
            />
        );
    };

    renderPickerContent = () => {
        const { isDateFocused } = this.state;
        const { view, withNow } = this.props;

        const style = {
            top: this.state.top,
            left: this.state.left,
        };

        if (view === VIEWS.WITH_POPUP) {
            if (!this.calendarLayer) {
                return null;
            }

            return ReactDOM.createPortal(
                <Popup
                    refCallback={this.refPopup}
                    setNow={this.setNow}
                    style={style}
                    visible={isDateFocused}
                    withNow={withNow}
                >
                    {this.renderCalendar()}
                </Popup>,
                this.calendarLayer
            );
        }

        if (view === VIEWS.WITHOUT_POPUP) {
            return this.renderCalendar();
        }

        return null;
    };

    render() {
        const { day, month, year, hour, minute, isTimeFocused, isDateFocused, isNowSelected } = this.state;
        const { mix, mode, disabled, view } = this.props;

        return (
            <div className={cx(b('datetimepicker', { disabled }), mix)} ref={this.rootRefCallback}>
                {view === VIEWS.WITH_POPUP && (
                    <Input
                        blurCallbacks={this.blurCallbacks}
                        changeCallbacks={this.changeCallbacks}
                        day={day}
                        focusCallbacks={this.focusCallbacks}
                        hour={hour}
                        isDateFocused={isDateFocused}
                        isNowSelected={isNowSelected}
                        isTimeFocused={isTimeFocused}
                        minute={minute}
                        mode={mode}
                        month={month}
                        refCallbacks={this.refCallbacks}
                        revealNow={this.revealNow}
                        year={year}
                    />
                )}
                {this.renderPickerContent()}
            </div>
        );
    }
}

export default Datetimepicker;
